import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { AxiosResponse } from 'axios';
import { authEventBus } from 'src/core/events/Events';
import type { EventAuthOnLogOut } from 'src/core/events/EventTypes';
import Constants from 'src/core/utils/Constants';
import type ApiUser from 'src/core/models/api/ApiUser';
import type ApiCreateOneTimePasswordResponse from 'src/core/models/api/responses/ApiCreateOneTimePasswordResponse';
import type ApiLoginResponse from 'src/core/models/api/responses/ApiLoginResponse';
import type ApiMeResponse from 'src/core/models/api/responses/ApiMeResponse';
import { reportOrRethrow } from 'src/core/utils/ApiCallbacks';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import CrashlyticsService from 'src/data/services/CrashlyticsService';
import StorageService from 'src/data/services/StorageService';
import TranslationService from 'src/data/services/TranslationService';
import { resetMemberDataStores } from 'src/domain/stores/MemberDataStores';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const DEFAULT_LANGUAGE: string = Constants.localeNl;

export const useAuthStore = create(
	combine(
		{
			isAuthenticated: false,
			user: null as ApiUser | null,
			didOnboarding: true,
			language: DEFAULT_LANGUAGE,
			forceLogin: false,
			subscribedToEvents: false,
		},

		(set, get) => {
			const init = async () => {
				_resetStore();

				if (
					Platform.OS === Constants.platformWeb &&
					typeof window !== 'undefined' &&
					window.location?.search?.includes(`${Constants.deeplinkMagicLinkParam}=`)
				) {
					await StorageService.clear();
				}

				const asyncStorageAcessToken = await AsyncStorage.getItem(Constants.legacyStorageAuthToken);
				if (asyncStorageAcessToken) {
					await StorageService.setAccessToken(asyncStorageAcessToken);
					await AsyncStorage.removeItem(Constants.legacyStorageAuthToken);
				}

				const asyncStorageOnboardingDone = await AsyncStorage.getItem(Constants.legacyStorageOnboardingDone);
				if (asyncStorageOnboardingDone) {
					await StorageService.setOnboardingDone(asyncStorageOnboardingDone === 'true');
					await AsyncStorage.removeItem(Constants.legacyStorageOnboardingDone);
				}

				const asyncStorageLanguage = await AsyncStorage.getItem(Constants.legacyStorageLanguage);
				if (asyncStorageLanguage) {
					await StorageService.setLanguage(asyncStorageLanguage);
					await AsyncStorage.removeItem(Constants.legacyStorageLanguage);
				}

				const user = await _getStorageUser();
				if (user) {
					set({ user });
					CrashlyticsService.setUser({ id: user.id });
				}

				const onboardingDone = await _getStorageOnboardingDone();
				if (onboardingDone) {
					set({ didOnboarding: onboardingDone });
				}

				const language =
					Platform.OS === Constants.platformWeb
						? TranslationService.language
						: ((await _getStorageLanguage()) ?? TranslationService.language);

				await setLanguage(language);

				const accessToken = await _getStorageAccessToken();
				if (accessToken) {
					_subscribeToEvents();

					set({ isAuthenticated: true });

					getApiUser().catch((error: ApiRequestError) => {
						CrashlyticsService.captureEvent({ message: 'Refreshing the user on app start failed.', extra: { error } });
					});
				}
			};

			const _resetStore = (forceLogin: boolean = false) => {
				_unsubscribeFromEvents();

				resetMemberDataStores();

				CrashlyticsService.clearUser();

				set({
					isAuthenticated: false,
					user: null,
					didOnboarding: true,
					language: DEFAULT_LANGUAGE,
					forceLogin,
				});
			};

			const _subscribeToEvents = () => {
				if (!get().subscribedToEvents) {
					authEventBus.on(Constants.eventAuthOnLogOut, _handleAuthEventOnLogOut);
					set({ subscribedToEvents: true });
				}
			};

			const _unsubscribeFromEvents = () => {
				authEventBus.off(Constants.eventAuthOnLogOut, _handleAuthEventOnLogOut);
				set({ subscribedToEvents: false });
			};

			const _handleAuthEventOnLogOut = ({ forceLogin }: EventAuthOnLogOut) => {
				if (get().isAuthenticated) {
					logout(forceLogin).catch((error: ApiRequestError) => {
						CrashlyticsService.captureEvent({ message: 'Logging out after a failed refresh failed.', extra: { error } });
					});
				}
			};

			const _endSession = async (context: string, forceLogin: boolean = false) => {
				_resetStore(forceLogin);

				await StorageService.clear().catch((error: ApiRequestError) => {
					CrashlyticsService.captureEvent({ message: `Clearing the storage on ${context} failed.`, extra: { error } });
				});
			};

			const logout = async (forceLogin: boolean = false) => {
				set({ isAuthenticated: false });

				const accessToken = await _getStorageAccessToken();

				await _endSession('logout', forceLogin);

				if (accessToken && !forceLogin) {
					_postApiLogout({
						accessToken,
						onError: () => {},
					});
				}
			};

			// The session survives a rejected password, so the teardown waits for the API to confirm the deletion.
			const deleteAccount = async ({
				password,
				onSuccess,
				onError,
			}: {
				password: string;
				onSuccess?: () => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
			}) => {
				const accessToken = await _getStorageAccessToken();

				// Without a token there is nothing to delete, so the session ends without reporting a deletion.
				if (!accessToken) {
					set({ isAuthenticated: false });

					await _endSession('account deletion');

					return;
				}

				await _postApiDeleteAccount({
					accessToken,
					password,
					onSuccess: async () => {
						set({ isAuthenticated: false });

						await _endSession('account deletion');
						await onSuccess?.();
					},
					onError,
				});
			};

			const getApiUser = async () => {
				await getApiMe({
					onSuccess: async (response) => {
						if (response?.data?.member) {
							set({ isAuthenticated: true });
							await setUser(response.data.member);
						}
					},
				});
			};

			const getApiMe = async ({
				accessToken,
				onSuccess,
				onError,
				onFinally,
			}: {
				accessToken?: string;
				onSuccess?: (response: AxiosResponse<ApiMeResponse>) => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.getMe(accessToken)
					.then(async (response) => await onSuccess?.(response))
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const postApiCheckEmail = async ({
				email,
				onSuccess,
				onError,
				onFinally,
			}: {
				email: string;
				onSuccess?: () => void;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.checkEmail(email)
					.then(() => onSuccess?.())
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const postApiRegisterAccount = async ({
				data,
				onSuccess,
				onError,
				onFinally,
			}: {
				data: Record<string, unknown>;
				onSuccess?: () => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.registerAccount(data)
					.then(async () => await onSuccess?.())
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const postApiLogin = async ({
				email,
				password,
				deviceName,
				onSuccess,
				onError,
				onFinally,
			}: {
				email: string;
				password: string;
				deviceName: string;
				onSuccess?: (response: AxiosResponse<ApiLoginResponse>) => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.login(email, password, deviceName)
					.then(async (response) => await onSuccess?.(response))
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const getApiVerifyMagicLink = async ({
				magicLink,
				onSuccess,
				onError,
				onFinally,
			}: {
				magicLink: string;
				onSuccess?: (response: AxiosResponse<ApiLoginResponse>) => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.verifyMagicLink(magicLink)
					.then(async (response) => await onSuccess?.(response))
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const postApiMagicLink = async ({
				email,
				onSuccess,
				onError,
				onFinally,
			}: {
				email: string;
				onSuccess?: () => void;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.requestMagicLink(email)
					.then(() => onSuccess?.())
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const postApiForgotPassword = async ({
				email,
				onSuccess,
				onError,
				onFinally,
			}: {
				email: string;
				onSuccess?: (response: AxiosResponse) => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.forgotPassword(email)
					.then(async (response) => await onSuccess?.(response))
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const postApiResendVerification = async ({
				email,
				onSuccess,
				onError,
				onFinally,
			}: {
				email: string;
				onSuccess?: () => void;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.resendEmailVerification(email)
					.then(() => onSuccess?.())
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const postApiCreateOneTimePassword = async ({
				type,
				onSuccess,
				onError,
				onFinally,
			}: {
				type: ApiCreateOneTimePasswordResponse['type'];
				onSuccess?: (response: AxiosResponse<ApiCreateOneTimePasswordResponse>) => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.createOneTimePassword(type)
					.then(async (response) => await onSuccess?.(response))
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const _postApiLogout = ({
				accessToken,
				onSuccess,
				onError,
				onFinally,
			}: {
				accessToken: string;
				onSuccess?: () => void;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				ThermenRepository.logout(accessToken)
					.then(() => onSuccess?.())
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const _postApiDeleteAccount = async ({
				accessToken,
				password,
				onSuccess,
				onError,
				onFinally,
			}: {
				accessToken: string;
				password: string;
				onSuccess?: () => void | Promise<void>;
				onError?: ((error: ApiRequestError) => void) | undefined;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.deleteAccount(accessToken, password)
					.then(async () => await onSuccess?.())
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const setToken = async (token: string) => {
				await _setStorageAccessToken({ accessToken: token });

				_subscribeToEvents();

				set({ isAuthenticated: true });
			};

			const setUser = async (user: ApiUser) => {
				await _setStorageUser({ user });
				set({ user });

				CrashlyticsService.setUser({ id: user.id });
			};

			const setDidOnboarding = async (didOnboarding: boolean) => {
				await _setStorageOnboardingDone({ onboardingDone: didOnboarding });
				set({ didOnboarding });
			};

			const setLanguage = async (language: string) => {
				await _setSecureStoreLanguage({ language });
				set({ language });
				await TranslationService.setLanguage(language);
			};

			const setForceLogin = (forceLogin: boolean) => set({ forceLogin });

			const _setStorageAccessToken = async ({ accessToken }: { accessToken: string }) =>
				await StorageService.setAccessToken(accessToken);

			const _getStorageAccessToken = async () => await StorageService.getAccessToken();

			const _setSecureStoreLanguage = async ({ language }: { language: string }) =>
				await StorageService.setLanguage(language);

			const _getStorageLanguage = async () => await StorageService.getLanguage();

			const _setStorageOnboardingDone = async ({ onboardingDone }: { onboardingDone: boolean }) =>
				await StorageService.setOnboardingDone(onboardingDone);

			const _getStorageOnboardingDone = async () => await StorageService.isOnboardingDone();

			const _setStorageUser = async ({ user }: { user: ApiUser }) => await StorageService.setUser(user);

			const _getStorageUser = async () => await StorageService.getUser();

			return {
				init,
				logout,
				deleteAccount,
				getApiMe,
				postApiCheckEmail,
				postApiRegisterAccount,
				postApiLogin,
				getApiVerifyMagicLink,
				postApiMagicLink,
				postApiForgotPassword,
				postApiResendVerification,
				postApiCreateOneTimePassword,
				setDidOnboarding,
				setLanguage,
				setToken,
				setUser,
				setForceLogin,
			};
		},
	),
);
